// Plan 24c Phase B, Task 6 — Disconnect self-panel screen (Screen 29)
//
// Full-viewport overlay displayed when the LOCAL player is disconnected.
// Shows a centered panel with title, message, and two action buttons:
//   - Reconnect (primary): navigates to 'reconnecting' by default
//   - Back to Warmup (ghost): navigates to the warmup arena by default
//
// ESC triggers the Back to Warmup action.
// No GSAP timelines — no spinner, no animation.
//
// Design tokens (docs/design/tokens.json):
//   typography.family.headline = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif"
//   typography.family.body     = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif"
//   color.text.danger          = "#E04040"
//   color.text.secondary       = "#B0B8C8"
//   color.ui.overlay-dim       = "rgba(0, 0, 0, 0.60)"
//   color.ui.panel-bg-alpha    = "rgba(30, 35, 50, 0.82)"
//   spacing.lg                 = "24px"
//   spacing.md                 = "16px"
//   z-index.loading            = 600

import { createButton } from '@client/ui/components/button';
import type { UIComponentHandle } from '@client/ui/components/types';
import type { ScreenHandle, ScreenMountContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type DisconnectPayload = {
  /** Optional override for the title; default 'Disconnected'. */
  title?: string;
  /** Optional override for the message; default 'You have been disconnected from the game.' */
  message?: string;
  /** Default: navigate to 'reconnecting'. */
  onReconnect?: () => void;
  /** Default: navigate to the warmup arena. */
  onBackToMenu?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createDisconnectScreen(ctx: ScreenMountContext<DisconnectPayload>): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { title, message, onReconnect, onBackToMenu } = payload;

  let disposed = false;

  // Collected button handles for disposal
  const buttonHandles: UIComponentHandle[] = [];

  // ── Default action callbacks ────────────────────────────────────────────────

  function doReconnect(): void {
    if (onReconnect !== undefined && onReconnect !== null) {
      onReconnect();
    } else {
      router.navigate('reconnecting', {});
    }
  }

  function doBackToMenu(): void {
    if (onBackToMenu !== undefined && onBackToMenu !== null) {
      onBackToMenu();
    } else {
      router.navigate('warmup-arena-overlay', { portal: null });
    }
  }

  // ── Build root overlay ──────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.setAttribute('data-screen', 'disconnect');
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '600', // z-index.loading
    background: 'rgba(0,0,0,0.60)', // color.ui.overlay-dim
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  // ── Build centered panel ────────────────────────────────────────────────────

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: '480px',
    padding: '24px 28px', // layout.panel-padding-lg
    borderRadius: '16px', // border-radius.xl
    background: 'rgba(30,35,50,0.82)', // color.ui.panel-bg-alpha
    boxShadow: '0 8px 32px rgba(0,0,0,0.50)', // shadow.modal
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  });

  // ── Title ───────────────────────────────────────────────────────────────────

  const titleEl = document.createElement('h2');
  titleEl.textContent = title ?? 'Disconnected';
  Object.assign(titleEl.style, {
    margin: '0',
    fontFamily: "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif", // typography.family.headline
    fontSize: '36px', // typography.size.h2
    color: '#E04040', // color.text.danger
    textAlign: 'center',
  });
  panel.appendChild(titleEl);

  // ── Message ──────────────────────────────────────────────────────────────────

  const messageEl = document.createElement('p');
  messageEl.textContent = message ?? 'You have been disconnected from the game.';
  Object.assign(messageEl.style, {
    margin: '0',
    marginTop: '24px', // spacing.lg
    fontFamily: "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif", // typography.family.body
    fontSize: '16px', // typography.size.body
    color: '#B0B8C8', // color.text.secondary
    textAlign: 'center',
  });
  panel.appendChild(messageEl);

  // ── Button row ───────────────────────────────────────────────────────────────

  const buttonRow = document.createElement('div');
  Object.assign(buttonRow.style, {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px', // spacing.md
    justifyContent: 'center',
    marginTop: '24px', // spacing.lg
  });

  // Reconnect (primary)
  const reconnectHandle = createButton({
    gsapRegistry,
    mount: undefined,
    variant: 'primary',
    size: 'default',
    label: 'Reconnect',
    onClick: () => {
      if (!disposed) doReconnect();
    },
  });
  buttonHandles.push(reconnectHandle);
  buttonRow.appendChild(reconnectHandle.element);

  // Back to Warmup (ghost)
  const backHandle = createButton({
    gsapRegistry,
    mount: undefined,
    variant: 'ghost',
    size: 'default',
    label: 'Back to Warmup',
    onClick: () => {
      if (!disposed) doBackToMenu();
    },
  });
  buttonHandles.push(backHandle);
  buttonRow.appendChild(backHandle.element);

  panel.appendChild(buttonRow);

  // ── Mount root ──────────────────────────────────────────────────────────────

  root.appendChild(panel);
  host.appendChild(root);

  // ── ESC handler ─────────────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (!disposed) doBackToMenu();
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ──────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);

    // Dispose all button handles
    for (const handle of buttonHandles) {
      handle.dispose();
    }

    // Remove root from DOM
    root.remove();
  }

  return { element: root, dispose };
}
